import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { MATRIX_FILTER_KEYS } from '../../shared/constants/matrix-filter-key.constants';
import { DashboardFilterBarComponent } from './components/dashboard-filter-bar/dashboard-filter-bar.component';
import type { DashboardFiltersValue } from './models/dashboard-filters.model';
import { FeatureComparisonCardComponent } from './components/feature-comparison-card/feature-comparison-card.component';
import { MatrixAnalyticsCardComponent } from './components/matrix-analytics-card/matrix-analytics-card.component';
import { MatrixInvestigationCardComponent } from './components/matrix-investigation-card/matrix-investigation-card.component';
import { MatrixTrackerCardComponent } from './components/matrix-tracker-card/matrix-tracker-card.component';
import { MidasAiVideosCardComponent } from './components/midas-ai-videos-card/midas-ai-videos-card.component';
import type { DashboardFilterOptions } from './models/dashboard-filters.model';
import { DASHBOARD_DATA } from './services/dashboard-data.token';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  templateUrl: './dashboard.component.html',
  imports: [
    CommonModule,
    MatrixAnalyticsCardComponent,
    MatrixTrackerCardComponent,
    MidasAiVideosCardComponent,
    MatrixInvestigationCardComponent,
    FeatureComparisonCardComponent,
    DashboardFilterBarComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit {
  private readonly dashboardData = inject(DASHBOARD_DATA);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  readonly filterOptions = signal<DashboardFilterOptions | null>(null);
  readonly infoIconHovered = signal(false);
  readonly filters = signal<DashboardFiltersValue>({
    favorite: 'favorites',
    medication: [],
    clinicalUnit: [],
    serviceDept: [],
  });

  readonly filtersAsRecord = computed<Record<string, string[]>>(() => {
    const { medication, clinicalUnit, serviceDept } = this.filters();
    const toValues = (arr: unknown): string[] => {
      if (!Array.isArray(arr)) return [];
      return arr.map((item) =>
        item !== null && typeof item === 'object'
          ? String((item as Record<string, unknown>)['value'] ?? '')
          : String(item)
      );
    };
    return {
      medication: toValues(medication),
      clinicalUnit: toValues(clinicalUnit),
      serviceDept: toValues(serviceDept),
    };
  });

  ngOnInit(): void {
    this.dashboardData
      .getFilterOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.filterOptions.set(options));
  }

  onFiltersChange(value: Record<string, unknown>): void {
    const med = MATRIX_FILTER_KEYS.MEDICATION;
    const loc = MATRIX_FILTER_KEYS.LOCATION;
    const svc = MATRIX_FILTER_KEYS.SERVICE_DEPARTMENT;
    this.filters.set({
      favorite: (value['favorite'] as string) ?? 'favorites',
      medication: (value[med] as string[]) ?? [],
      clinicalUnit: (value[loc] as string[]) ?? [],
      serviceDept: (value[svc] as string[]) ?? [],
    });
  }

  getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) {
      return this.translate.instant('dashboard.goodMorning');
    } else if (hour < 17) {
      return this.translate.instant('dashboard.goodAfternoon');
    } else {
      return this.translate.instant('dashboard.goodEvening');
    }
  }
}


